import React, { useEffect, useState } from "react";
import Icon from "../../components/Icon";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Box from "../../components/Box";
import Button from "../../components/Button";
import Table from "../../components/Table";
import Input from "../../components/Input";
import InputSelect from "../../components/InputSelect";
import PageFooter from "../../components/PageFooter";
import PanelTemplate from "../../templates/Panel";
import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import { Loading } from "../../components/Loading";
import { toast } from "react-toastify";
import Modal from "../../components/Modal";
import { createOptions } from "../../helpers/selects";
import { convertIntToMoney, convertMoenyToInt } from "../../helpers/conversions";
import { ModalDelete } from "../../components/ModalDelete";
import { hasPermission } from "../../services/auth";
import { calculateSizeUnits, calculateSizeCustomizationTotal } from "../Quote/ModalProduct/customizationCalculate";

const interval = 99;

const types = [
    {label: 'Milheiro', value: 'thousand'},
    {label: 'Unidade/Intervalo', value: 'unit'},
    {label: 'Tamanho', value: 'size'}
];

const status = [
    {label: 'Inativa', value: false},
    {label: 'Ativa', value: true}
];

const multiplyColorsOptions = [
    {label: 'Não', value: false},
    {label: 'Sim', value: true}
];

const supplierTypes = [
    {label: 'Pessoa jurídica', value: 'PJ'},
    {label: 'Pessoa física', value: 'PF'}
];

const costTypes = [
    {label: 'Por unidade', value: 'unit'},
    {label: 'Por intervalo', value: 'interval'}
];

const sizeTypes = [
    {label: 'Folha', value: 'sheet'},
    {label: 'Rolo', value: 'roll'}
];

const sizeTypeLabel = type => sizeTypes.find(sizeType => sizeType.value === type)?.label ?? type;

export const CustomizationEdit = () => {
    const { id } = useParams();
    const navigete = useNavigate();
    const location = useLocation();

    const [loading, setLoading] = useState(true);
    const [companies, setCompanies] = useState([]);
    const [customers, setCustomers] = useState([]);
    
    const [customizationName, setCustomizationName] = useState('');
    const [customizationCalculationPer, setCustomizationCalculationPer] =  useState(null);
    const [customizationStatus, setCustomizationStatus] = useState(null);
    const [customizationCompanies, setCustomizationCompanies] = useState([]);
    const [customizationCustomers, setCustomizationCustomers] = useState([]);
    const [customizationMultiplyColors, setCustomizationMultiplyColors] = useState(null);
    const [customizationDefaultSpacingMm, setCustomizationDefaultSpacingMm] = useState('');
    const [customizationSizes, setCustomizationSizes] = useState([]);

    const [simQuantity, setSimQuantity] = useState('');
    const [simArtWidth, setSimArtWidth] = useState('');
    const [simArtHeight, setSimArtHeight] = useState('');
    const [simSpacing, setSimSpacing] = useState('');

    const [showSizeModal, setShowSizeModal] = useState(false);
    const [sizeToEdit, setSizeToEdit] = useState(null);
    const [sizeName, setSizeName] = useState('');
    const [sizeWidth, setSizeWidth] = useState('');
    const [sizeHeight, setSizeHeight] = useState('');
    const [sizeType, setSizeType] = useState(null);
    const [sizeToDelete, setSizeToDelete] = useState(null);

    const [showCreateCost, setShowCreateCost] = useState(false);

    const [costSupplierType, setCostSupplierType] = useState('PJ');
    const [costCompanyId, setCostCompanyId] = useState(null);
    const [costCustomerId, setCostCustomerId] = useState(null);
    const [costSizeId, setCostSizeId] = useState(null);
    const [costFixedFee, setCostFixedFee] = useState('R$ 0,00');
    const [costApplicationCost, setCostApplicationCost] = useState('R$ 0,00');
    const [costVariations, setCostVariations] = useState([]);

    const [customizationVariationToEdit, setCustomizationVariationToEdit] = useState(null);
    const [customizationVariationCost, setCustomizationVariationCost] = useState('');

    const [customizationVariationToDelete, setCustomizationVariationToDelete] = useState(null);
    const [companyToDelete, setCompanyToDelete] = useState(null);
    const [customerToDelete, setCustomerToDelete] = useState(null);

    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        getCustomization();
        getCompanies();
        getCustomers();
    }, []);

    useEffect(() => {
        getCostVariations();
    }, [costCompanyId, costCustomerId, costSizeId]);

    useEffect(() => {
        if(!showCreateCost) clean();
    }, [showCreateCost])

    useEffect(() => {
        if(!showSizeModal) cleanSize();
    }, [showSizeModal])

    useEffect(() => {
        clean();
    }, [costSupplierType])

    const getCompanies = () => {
        api.get(`/company?filters[0][type][contain]=customization`).then(res => {
            const resources = res.data.resources;
            setCompanies(resources);
        }).catch(error => {
            renderError(error);
        });
    }

    const getCustomers = () => {
        api.get(`/customer?filters[0][type][contain]=customization`).then(res => {
            const resources = res.data.resources.data;
            setCustomers(resources);
        }).catch(error => {
            renderError(error);
        });
    }

    const getCustomization = () => {
        setLoading(true);

        api.get(`/customization/${id}`).then(res => {
            const customization = res.data.resource;
            setCustomizationName(customization.name);
            setCustomizationStatus(customization.status);
            setCustomizationMultiplyColors(customization.multiply_colors);
            setCustomizationCalculationPer(customization.calculation_per);
            setCustomizationDefaultSpacingMm(customization.default_spacing_mm ?? '');
            setCustomizationSizes(customization.sizes ?? []);
            getCustomizationCompanies(customization.companies, customization.calculation_per);
            getCustomizationCustomers(customization.customers, customization.calculation_per);
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    }

    const getCustomizationListReturnUrl = () => {
        const params = new URLSearchParams(location.search);
        const returnTo = params.get('return_to');

        return returnTo && returnTo.startsWith('/customization/list') ? returnTo : '/customization/list';
    }

    const deleteCustomization = () => {
        setLoading(true);

        api.delete(`/customization/${id}`).then(res => {
            navigete(getCustomizationListReturnUrl());
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    const sortCosts = (costs, calculationPer) => {
        return costs.sort((a, b) => {
            if(calculationPer === 'thousand'){
                return a.thousand - b.thousand;
            } else {
                return a.init_qtd - b.init_qtd;
            }
        });
    }

    // No modo "size" um mesmo fornecedor pode ter tabelas para várias medidas:
    // cada par fornecedor + medida vira um grupo próprio.
    const getSupplierGroupKey = (supplierId, pivot, calculationPer) => {
        return calculationPer === 'size' ? `${supplierId}-${pivot.customization_size_id}` : `${supplierId}`;
    }

    const getCustomizationCompanies = (companies, calculationPer) => {
        let toCustomizationCompanies = [];

        companies.map(company => {
            const key = getSupplierGroupKey(company.id, company.pivot, calculationPer);
            const group = toCustomizationCompanies.find(c => c.key === key);

            if(!group){
                toCustomizationCompanies.push({
                    key,
                    id: company.id,
                    cnpj: company.cnpj,
                    trade_name: company.trade_name,
                    customization_size_id: company.pivot.customization_size_id ?? null,
                    fixed_fee: company.pivot.fixed_fee ?? null,
                    application_cost: company.pivot.application_cost ?? null,
                    costs: [company.pivot]
                });
            } else {
                group.costs.push(company.pivot);
                group.costs = sortCosts(group.costs, calculationPer);
            }
        });

        setCustomizationCompanies(toCustomizationCompanies);
    }

    const getCustomizationCustomers = (customers, calculationPer) => {
        let toCustomizationCustomers = [];

        customers.map(customer => {
            const key = getSupplierGroupKey(customer.id, customer.pivot, calculationPer);
            const group = toCustomizationCustomers.find(c => c.key === key);

            if(!group){
                toCustomizationCustomers.push({
                    key,
                    id: customer.id,
                    cpf: customer.cpf,
                    name: customer.name,
                    customization_size_id: customer.pivot.customization_size_id ?? null,
                    fixed_fee: customer.pivot.fixed_fee ?? null,
                    application_cost: customer.pivot.application_cost ?? null,
                    costs: [customer.pivot]
                });
            } else {
                group.costs.push(customer.pivot);
                group.costs = sortCosts(group.costs, calculationPer);
            }
        });

        setCustomizationCustomers(toCustomizationCustomers);
    }

    const updateCustomization = () => {
        setLoading(true);

        api.put(`/customization/${id}`, {
            name: customizationName,
            status: customizationStatus,
            calculation_per: customizationCalculationPer,
            multiply_colors: customizationCalculationPer === 'size' ? (customizationMultiplyColors ?? false) : customizationMultiplyColors,
            default_spacing_mm: customizationCalculationPer === 'size' && customizationDefaultSpacingMm !== '' ? customizationDefaultSpacingMm : null
        }).then(res => {
            toast.success('Gravação atualizada com sucesso');
            getCustomization();
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    const createSize = () => {
        setShowSizeModal(true);
    }

    const editSize = size => {
        setSizeToEdit(size);
        setSizeName(size.name);
        setSizeWidth(size.width_mm);
        setSizeHeight(size.height_mm);
        setSizeType(size.type);
        setShowSizeModal(true);
    }

    const storeSize = () => {
        setLoading(true);

        const data = {
            name: sizeName,
            width_mm: sizeWidth,
            height_mm: sizeHeight,
            type: sizeType
        };

        const request = sizeToEdit
            ? api.put(`/customization/size/${sizeToEdit.id}`, data)
            : api.post(`/customization/${id}/size`, data);

        request.then(res => {
            toast.success(sizeToEdit ? 'Medida atualizada com sucesso' : 'Medida cadastrada com sucesso');
            setShowSizeModal(false);
            getCustomization();
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    const deleteSize = () => {
        setLoading(true);

        api.delete(`/customization/size/${sizeToDelete}`).then(res => {
            setSizeToDelete(null);
            getCustomization();
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    const cleanSize = () => {
        setSizeToEdit(null);
        setSizeName('');
        setSizeWidth('');
        setSizeHeight('');
        setSizeType(null);
    }

    const createCost = () => {
        setShowCreateCost(true);
    }

    const storeCost = () => {
        setLoading(true);

        const variations = [];

        costVariations.map(costVariation => {
            if(customizationCalculationPer === 'thousand'){
                variations.push({
                    cost: convertMoenyToInt(costVariation.cost),
                    cost_type: null,
                    thousand: costVariation.thousand
                });
            } else if(customizationCalculationPer === 'size'){
                variations.push({
                    cost: convertMoenyToInt(costVariation.cost),
                    cost_type: null,
                    init_qtd: costVariation.startQuantity,
                    // Quant. final vazia = faixa aberta ("acima de X")
                    final_qtd: Number.isInteger(costVariation.endQuantity) ? costVariation.endQuantity : null
                });
            } else {
                variations.push({
                    cost: convertMoenyToInt(costVariation.cost),
                    cost_type: costVariation.costType,
                    init_qtd: costVariation.startQuantity,
                    final_qtd: costVariation.endQuantity
                });
            }
        });

        const data = {
            supplier_type: costSupplierType,
            customer_id: costSupplierType === "PF" ?  costCustomerId : null,
            company_id: costSupplierType === "PJ" ?  costCompanyId : null,
            customization_id: id,
            type: customizationCalculationPer,
            variations
        }

        if(customizationCalculationPer === 'size'){
            data.customization_size_id = costSizeId;
            data.fixed_fee = convertMoenyToInt(costFixedFee);
            data.application_cost = convertMoenyToInt(costApplicationCost);
        }

        api.post(`/customization/variation`, data).then(res => {
            toast.success('Selo cadastrado com sucesso');
            setShowCreateCost(false);
            getCustomization();
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    const getCostVariations = () => {
        if(costCompanyId === null && costCustomerId === null){
            setCostVariations([]);
            return;
        }

        const variations = [];
        let costs = [];
        let groups = [];

        if(costSupplierType === "PJ"){
            groups = customizationCompanies.filter(c => c.id === costCompanyId);
        } else {
            groups = customizationCustomers.filter(c => c.id === costCustomerId);
        }

        if(customizationCalculationPer === 'size'){
            // grupos são por fornecedor + medida: usa só as faixas da medida selecionada
            const group = groups.find(g => g.customization_size_id === costSizeId);
            costs = group?.costs ? group.costs : [];
        } else {
            costs = groups[0]?.costs ? groups[0].costs : [];
        }

        if(customizationCalculationPer === 'thousand'){
            const thousand = costs.length > 0 ? costs.at(-1).thousand + 1 : 1;
            variations.push({
                cost: 'R$ 0,00',
                costType: 'unit',
                thousand
            });
        }
        if(customizationCalculationPer === 'unit'){
            const startQuantity = costs.length > 0 ? costs.at(-1).final_qtd + 1 : 1;
            variations.push({
                cost: 'R$ 0,00',
                costType: 'unit',
                startQuantity,
                endQuantity: startQuantity + interval
            });
        }
        if(customizationCalculationPer === 'size'){
            const lastFinal = costs.length > 0 ? costs.at(-1).final_qtd : null;
            const startQuantity = Number.isInteger(lastFinal) ? lastFinal + 1 : 1;
            variations.push({
                cost: 'R$ 0,00',
                costType: null,
                startQuantity,
                endQuantity: startQuantity + interval
            });
        }
        setCostVariations(variations);
    }

    const clean = () => {
        setCostCustomerId(null);
        setCostCompanyId(null);
        setCostSizeId(null);
        setCostFixedFee('R$ 0,00');
        setCostApplicationCost('R$ 0,00');
        setCostVariations([]);
    }

    const handleChangeCost = (index, value) => {
        const toCostVariations = costVariations;
        toCostVariations[index]['cost'] = value;
        setCostVariations([...toCostVariations]);
    }

    const handleChangeCostType = (index, value) => {
        const toCostVariations = costVariations;
        toCostVariations[index]['costType'] = value;
        setCostVariations([...toCostVariations]);
    }

    const deleteCostVariation = () => {
        let toCostVariations = costVariations;
        toCostVariations.splice(-1);
        setCostVariations([...toCostVariations]);
    }

    const handleChangeStartQuantity = (index, value) => {
        const toCostVariations = costVariations;
        toCostVariations[index]['startQuantity'] = value;
        setCostVariations([...toCostVariations]);
    }

    const handleChangeEndQuantity = (index, value) => {
        const toCostVariations = costVariations;
        toCostVariations[index]['endQuantity'] = value;
        setCostVariations([...toCostVariations]);
    }

    // Sem máscara convertToInt: precisa aceitar vazio (faixa aberta "acima de X")
    const handleChangeEndQuantityAllowEmpty = (index, value) => {
        const digits = value.toString().replace(/\D/g, '');
        handleChangeEndQuantity(index, digits === '' ? '' : parseInt(digits, 10));
    }

    const addCostVariation = () => {
        const toCostVariations = costVariations;

        if(customizationCalculationPer === 'thousand'){
            const thousand = costVariations[costVariations.length - 1].thousand + 1;
            toCostVariations.push({
                cost: 'R$ 0,00',
                costType: null,
                thousand
            });
        }
        if(customizationCalculationPer === 'unit'){
            const last = costVariations[costVariations.length - 1].endQuantity;
            let endQuantity = 0;

            if(Number.isInteger(last)){
                endQuantity = last + 1;
            }

            const start = costVariations[costVariations.length - 1].thousand + 1;
            toCostVariations.push({
                cost: 'R$ 0,00',
                costType: 'unit',
                startQuantity: endQuantity,
                endQuantity: endQuantity + interval
            })
        }
        if(customizationCalculationPer === 'size'){
            const last = costVariations[costVariations.length - 1].endQuantity;
            const startQuantity = Number.isInteger(last) ? last + 1 : 1;
            toCostVariations.push({
                cost: 'R$ 0,00',
                costType: null,
                startQuantity,
                endQuantity: startQuantity + interval
            });
        }

        setCostVariations([...toCostVariations]);
    }

    const deleteCustomizationVariation = () => {
        setLoading(true);
        
        api.delete(`/customization/variation/${customizationVariationToDelete}`).then(res => {
            setCustomizationVariationToDelete(null);
            getCustomization();
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    // Simulador: mesma conta do orçamento (fornecedor x medida mais barato),
    // considerando 1 gravação por peça
    const getSimulation = () => {
        const quantity = simQuantity;
        const artWidth = simArtWidth;
        const artHeight = simArtHeight;

        if(!quantity || !artWidth || !artHeight) return null;

        const spacing = simSpacing !== '' ? simSpacing : (customizationDefaultSpacingMm !== '' ? customizationDefaultSpacingMm : 0);

        const groups = [
            ...customizationCompanies.map(group => ({...group, supplierName: group.trade_name})),
            ...customizationCustomers.map(group => ({...group, supplierName: group.name}))
        ];

        let best = null;

        groups.forEach(group => {
            const size = customizationSizes.find(s => s.id === group.customization_size_id);
            if(!size) return;

            const units = calculateSizeUnits(size, artWidth, artHeight, spacing, quantity);
            if(units === null) return;

            const band = group.costs.find(cost => cost.init_qtd <= units && (cost.final_qtd === null || units <= cost.final_qtd));
            if(!band) return;

            const fixedFee = band.fixed_fee ?? group.fixed_fee ?? 0;
            const applicationCost = band.application_cost ?? group.application_cost ?? 0;
            const total = calculateSizeCustomizationTotal(units, band.cost, fixedFee, applicationCost, quantity);

            if(best === null || total < best.total){
                best = { size, units, cost: band.cost, fixedFee, applicationCost, total, supplierName: group.supplierName };
            }
        });

        if(best === null){
            const fitsSomeSize = customizationSizes.some(size => calculateSizeUnits(size, artWidth, artHeight, spacing, quantity) !== null);
            if(customizationSizes.length === 0){
                return { error: 'Cadastre ao menos uma medida para simular.' };
            }
            if(!fitsSomeSize){
                return { error: 'A arte não cabe em nenhuma medida cadastrada.' };
            }
            if(groups.length === 0){
                return { error: 'Cadastre os custos de um fornecedor para simular.' };
            }
            return { error: 'Nenhuma faixa de custo atende essa simulação — confira as faixas cadastradas.' };
        }

        const unitLabel = best.size.type === 'roll' ? 'metro(s)' : 'folha(s)';
        const applicationText = best.applicationCost ? ` + aplicação ${quantity} × R$ ${convertIntToMoney(best.applicationCost)}` : '';
        return {
            text: `${best.size.name} — ${best.units} ${unitLabel} × R$ ${convertIntToMoney(best.cost)} + taxa fixa R$ ${convertIntToMoney(best.fixedFee)}${applicationText} = R$ ${convertIntToMoney(best.total)} · Fornecedor: ${best.supplierName}`
        };
    }

    // No método size cada card é fornecedor x medida: envia a medida para não
    // apagar as tabelas das outras medidas do mesmo fornecedor
    const supplierDeleteQuery = toDelete =>
        toDelete?.customization_size_id ? `?customization_size_id=${toDelete.customization_size_id}` : '';

    const deleteCompany = () => {
        setLoading(true);

        api.delete(`/customization/${id}/company/${companyToDelete.id}${supplierDeleteQuery(companyToDelete)}`).then(res => {
            setCompanyToDelete(null);
            getCustomization();
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    const deleteCustomer = () => {
        setLoading(true);

        api.delete(`/customization/${id}/customer/${customerToDelete.id}${supplierDeleteQuery(customerToDelete)}`).then(res => {
            setCustomerToDelete(null);
            getCustomization();
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    const editVariationCost = cost => {
        setCustomizationVariationCost( `R$ ${convertIntToMoney(cost.cost)}` );
        setCustomizationVariationToEdit(cost);
    }

    const storeVariationCost = () => {
        setLoading(true);

        api.put(`/customization/variation/${customizationVariationToEdit.id}`, {
            cost: convertMoenyToInt(customizationVariationCost)
        }).then(res => {
            toast.success('Custo atualizado com sucesso');
            getCustomization();
            setCustomizationVariationToEdit(null);
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    }

    return(
        <>
            <ModalDelete
                show={confirmDelete}
                action={deleteCustomization}
                close={() => setConfirmDelete(false)}
                loading={loading}
            />
            <ModalDelete 
                show={companyToDelete !== null}
                action={deleteCompany}
                close={() => setCompanyToDelete(null)}
                loading={loading}
            />
            <ModalDelete 
                show={customerToDelete !== null}
                action={deleteCustomer}
                close={() => setCustomerToDelete(null)}
                loading={loading}
            />
            <ModalDelete
                show={customizationVariationToDelete !== null}
                action={deleteCustomizationVariation}
                close={() => setCustomizationVariationToDelete(null)}
                loading={loading}
            />
            <ModalDelete
                show={sizeToDelete !== null}
                action={deleteSize}
                close={() => setSizeToDelete(null)}
                loading={loading}
            />
            <Modal
                title={sizeToEdit ? `Alterar medida` : `Cadastrar medida`}
                show={showSizeModal}
                size={`medium`}
                close={() => setShowSizeModal(false)}
                footer={
                    <Button
                        type={`primary`}
                        size={`small`}
                        text={sizeToEdit ? `Salvar alterações` : `Cadastrar`}
                        svg={`save-white`}
                        action={storeSize}
                        loading={loading}
                    />
                }
            >
                <div className="section">
                    <div className="row">
                        <div className="col-6">
                            <Input type={`text`} label={`Nome *`} value={sizeName} change={setSizeName} />
                        </div>
                        <div className="col-6">
                            <InputSelect label={`Tipo *`} value={sizeType} change={setSizeType} options={sizeTypes} />
                        </div>
                        <div className="col-6">
                            <Input label={`Largura (mm) *`} mask={`convertToInt`} value={sizeWidth} change={setSizeWidth} />
                        </div>
                        <div className="col-6">
                            <Input label={`Altura (mm) *`} mask={`convertToInt`} value={sizeHeight} change={setSizeHeight} />
                        </div>
                    </div>
                    <p className="input-hint">
                        <strong>Folha:</strong> cobrada por folha; as artes precisam caber dentro dela (ex.: UV A4 = 210×297mm).
                        <br />
                        <strong>Rolo:</strong> largura fixa e cobrança por trecho; a altura é o passo de cobrança (ex.: têxtil 580×1000mm = rolo de 580mm cobrado por metro).
                    </p>
                </div>
            </Modal>
            <Modal
                title={`Cadastrar custo`}
                show={showCreateCost}
                size={`medium`}
                close={() => setShowCreateCost(false)}
                footer={
                    <Button 
                        type={`primary`}
                        size={`small`}
                        text={`Cadastrar`}
                        svg={`save-white`}
                        action={storeCost}
                        loading={loading}
                    />
                }
            >
                <div className="section">
                    {showCreateCost && // força abrir sem nenhuma opção selecionada
                        <>
                            <div className="row">
                                <div className="col">
                                        <InputSelect 
                                            label={`Tipo de fornecedor`}
                                            change={setCostSupplierType}
                                            value={costSupplierType}
                                            options={supplierTypes} 
                                        />
                                </div>
                            </div>
                            {costSupplierType === "PJ" &&
                                <div className="row">
                                    <div className="col">
                                            <InputSelect 
                                                label={`Fornecedor`}
                                                change={setCostCompanyId}
                                                value={costCompanyId}
                                                options={createOptions(companies, 'trade_name', 'id')} 
                                                />
                                    </div>
                                </div>
                            }
                            {costSupplierType === "PF" &&
                                <div className="row">
                                    <div className="col">
                                        <InputSelect
                                            label={`Fornecedor`}
                                            change={setCostCustomerId}
                                            value={costCustomerId}
                                            options={createOptions(customers, 'name', 'id')}
                                        />
                                    </div>
                                </div>
                            }
                            {customizationCalculationPer === 'size' &&
                                <>
                                    <div className="row">
                                        <div className="col">
                                            <InputSelect
                                                label={`Medida *`}
                                                change={setCostSizeId}
                                                value={costSizeId}
                                                options={customizationSizes.map(size => ({
                                                    label: `${size.name} (${size.width_mm}x${size.height_mm}mm)`,
                                                    value: size.id
                                                }))}
                                            />
                                        </div>
                                        <div className="col">
                                            <Input
                                                label={`Taxa fixa`}
                                                mask={`convertToMoney`}
                                                value={costFixedFee}
                                                change={setCostFixedFee}
                                            />
                                        </div>
                                        <div className="col">
                                            <Input
                                                label={`Aplicação por peça`}
                                                mask={`convertToMoney`}
                                                value={costApplicationCost}
                                                change={setCostApplicationCost}
                                            />
                                        </div>
                                    </div>
                                    <p className="input-hint">
                                        <strong>Taxa fixa:</strong> somada uma vez por uso desta gravação com este fornecedor (ex.: frete, setup, custo de arte).
                                        <br />
                                        <strong>Aplicação por peça:</strong> cobrada em cada peça (ex.: prensar a estampa em cada camiseta).
                                        <br />
                                        <strong>Faixas de custo:</strong> preço por folha/metro conforme a quantidade consumida — deixe a última "Quant. final" em branco para valer "em diante".
                                    </p>
                                </>
                            }
                        </>
                    }
                </div>
                <h2 className="section-title" style={{ marginBottom: 20 }}>Faixas de custo</h2>
                {(costCompanyId === null && costCustomerId === null) &&
                    <div className="cost-bands-empty">
                        Primeiro, escolha o fornecedor para cadastrar as faixas de custo.
                    </div>
                }
                {costVariations.map((costVariation, index) => (
                    <div key={index} className="section">
                        <div className="row">
                            {customizationCalculationPer === 'thousand' &&
                                <div className="col-6">
                                    <Input label={`Milheiro`} value={`${costVariation.thousand}º Milheiro`} disabled={true} />
                                </div>
                            }
                            {customizationCalculationPer === 'unit' &&
                                <>
                                    <div className="col">
                                        <Input
                                            label={`Quant. inicial`}
                                            value={costVariation.startQuantity}
                                            mask={`convertToInt`}
                                            change={value => handleChangeStartQuantity(index, value)}
                                        />
                                    </div>
                                    <div className="col">
                                        <Input
                                            label={`Quant. final`}
                                            value={costVariation.endQuantity}
                                            mask={`convertToInt`}
                                            change={value => handleChangeEndQuantity(index, value)}
                                        />
                                    </div>
                                </>
                            }
                            {customizationCalculationPer === 'size' &&
                                <>
                                    <div className="col">
                                        <Input
                                            label={`Quant. inicial`}
                                            value={costVariation.startQuantity}
                                            mask={`convertToInt`}
                                            change={value => handleChangeStartQuantity(index, value)}
                                        />
                                    </div>
                                    <div className="col">
                                        <Input
                                            label={`Quant. final`}
                                            placeholder={`Em diante`}
                                            value={costVariation.endQuantity}
                                            change={value => handleChangeEndQuantityAllowEmpty(index, value)}
                                        />
                                    </div>
                                </>
                            }
                            <div className="col">
                               <Input
                                    label={customizationCalculationPer === 'size' ? `Custo por unidade` : `Custo`}
                                    // right={customizationCalculationPer === 'unit' ? <span>por unidade</span> : null}
                                    mask={`convertToMoney`}
                                    value={costVariation.cost}
                                    change={value => handleChangeCost(index, value)}
                                />
                            </div>
                            {customizationCalculationPer === 'unit' &&
                                <div className="col">
                                    <InputSelect 
                                        label={`Cálculo por`}
                                        options={costTypes}
                                        value={costVariation.costType} 
                                        change={value => handleChangeCostType(index, value)} 
                                    />
                                </div>
                            }
                            <div className="col-1 col-customization-delete">
                                {index > 0 && index === (costVariations.length -1) &&
                                    <Button type={`delete`} svg={`delete-small`} action={deleteCostVariation} />
                                }
                            </div>
                        </div>
                    </div>
                ))}
                {costVariations.length > 0 &&
                    <Button 
                        type={`link`}
                        size={`small`}
                        text={`Cadastrar mais variações de custo`}
                        full={true}
                        action={addCostVariation}
                        loading={loading}
                    />
                }
            </Modal>
            <Modal
                title={`Alterar custo`}
                show={customizationVariationToEdit !== null}
                loading={loading}
                close={() => setCustomizationVariationToEdit(null)}
                footer={
                    <Button 
                        type={`primary`}
                        size={`small`}
                        text={`Salvar alterações`}
                        svg={`save-white`}
                        action={storeVariationCost}
                        loading={loading}
                    />
                }
            >
                <div className="section">
                    <div className="row">
                        <div className="col-5">
                            <Input 
                                label={`Custo`}
                                // right={customizationCalculationPer === 'unit' ? <span>por unidade</span> : null}
                                mask={`convertToMoney`} 
                                value={customizationVariationCost} 
                                change={setCustomizationVariationCost} 
                            />
                        </div>
                    </div>
                </div>
            </Modal>
            <PanelTemplate id="customization-edit">
                <Loading show={loading} />
                {!loading &&
                    <>
                        <Box>
                            <h2 className="section-title">Informações gerais</h2>
                            <div className="row">
                                <div className="col-3">
                                    <InputSelect label={`Status`} value={customizationStatus} change={setCustomizationStatus} options={status} />
                                </div>
                                <div className="col-3">
                                    <InputSelect isDisabled={true} label={`Cálculo por`} value={customizationCalculationPer} change={setCustomizationCalculationPer} options={types} />
                                </div>
                                <div className="col-3">
                                    <Input type={`text`} label={`Nome`} value={customizationName} change={setCustomizationName} />
                                </div>
                                {customizationCalculationPer !== 'size' &&
                                    <div className="col-3">
                                    <InputSelect
                                        label={`Multiplicar pelas cores`}
                                        value={customizationMultiplyColors}
                                        change={setCustomizationMultiplyColors}
                                        options={multiplyColorsOptions}
                                    />
                                    </div>
                                }
                                {customizationCalculationPer === 'size' &&
                                    <div className="col-3">
                                        <Input
                                            label={`Espaçamento padrão (mm)`}
                                            mask={`convertToInt`}
                                            value={customizationDefaultSpacingMm}
                                            change={setCustomizationDefaultSpacingMm}
                                        />
                                        <p className="input-hint">Folga entre uma arte e outra na impressão. Vem preenchido no orçamento, mas pode ser ajustado lá.</p>
                                    </div>
                                }
                            </div>

                            {customizationCalculationPer === 'size' &&
                                <>
                                    <h2 className="section-title">Medidas</h2>
                                    <p className="input-hint">A medida é a área de impressão que o fornecedor cobra: uma folha (ex.: A4) ou um rolo (cobrado por metro). O custo do orçamento vem de quantas folhas ou metros as artes ocupam.</p>
                                    {hasPermission('edit_customization') &&
                                        <Button type={`secondary`} size={`small`} svg={`plus-blue`} text={`Cadastrar medida`} action={createSize} />
                                    }
                                    <div className="sizes-table" style={{ marginTop: 15, marginBottom: 25 }}>
                                    <Table
                                        header={['Nome', 'Largura (mm)', 'Altura (mm)', 'Tipo', ...(hasPermission('edit_customization') ? ['', ''] : [])]}
                                        rows={customizationSizes.map(size => ({
                                            data: [
                                                size.name,
                                                size.width_mm,
                                                size.height_mm,
                                                sizeTypeLabel(size.type)
                                            ],
                                            buttons: hasPermission('edit_customization') ? [
                                                <Button type={`transparent`} svg={`edit`} action={() => editSize(size)} />,
                                                <Button type={`delete`} svg={`delete`} action={() => setSizeToDelete(size.id)} />
                                            ] : [],
                                            id: size.id
                                        }))}
                                        loading={false}
                                    />
                                    </div>

                                    {/* Simulador "Testar cálculo" oculto temporariamente — reativar removendo o comentário
                                    <h2 className="section-title">Testar cálculo</h2>
                                    <p className="input-hint">Simule uma arte para conferir se as medidas e os custos estão certos. O resultado usa o fornecedor mais barato, considerando 1 gravação por peça.</p>
                                    <div className="row">
                                        <div className="col-3">
                                            <Input label={`Quantidade de peças`} mask={`convertToInt`} value={simQuantity} change={setSimQuantity} />
                                        </div>
                                        <div className="col-3">
                                            <Input label={`Largura da arte (mm)`} mask={`convertToInt`} value={simArtWidth} change={setSimArtWidth} />
                                        </div>
                                        <div className="col-3">
                                            <Input label={`Altura da arte (mm)`} mask={`convertToInt`} value={simArtHeight} change={setSimArtHeight} />
                                        </div>
                                        <div className="col-3">
                                            <Input label={`Espaçamento (mm)`} placeholder={customizationDefaultSpacingMm !== '' ? `${customizationDefaultSpacingMm}` : `0`} value={simSpacing} change={value => {
                                                const digits = value.toString().replace(/\D/g, '');
                                                setSimSpacing(digits === '' ? '' : parseInt(digits, 10));
                                            }} />
                                        </div>
                                    </div>
                                    {(() => {
                                        const simulation = getSimulation();
                                        if(!simulation) return null;
                                        return simulation.error
                                            ? <p className="simulator-result simulator-result--error">{simulation.error}</p>
                                            : <p className="simulator-result">{simulation.text}</p>;
                                    })()}
                                    */}
                                </>
                            }

                            <h2 className="section-title">Relação de custos</h2>
                            {hasPermission('edit_customization') &&
                                <Button type={`secondary`} size={`small`} svg={`plus-blue`} text={`Cadastrar custo`} action={createCost} />
                            }
                            <div className="row">
                                {customizationCompanies.map((customizationCompany, index) => (
                                    <Customization
                                        key={index}
                                        index={index}
                                        customizationCompany={customizationCompany}
                                        customizationCustomer={null}
                                        setCompanyToDelete={setCompanyToDelete}
                                        editVariationCost={editVariationCost}
                                        setCustomizationVariationToDelete={setCustomizationVariationToDelete}
                                        customizationCalculationPer={customizationCalculationPer}
                                        customizationSizes={customizationSizes}
                                    />
                                ))}
                                {customizationCustomers.map((customizationCustomer, index) => (
                                    <Customization
                                        key={index}
                                        index={index}
                                        customizationCompany={null}
                                        customizationCustomer={customizationCustomer}
                                        setCompanyToDelete={setCompanyToDelete}
                                        setCustomerToDelete={setCustomerToDelete}
                                        editVariationCost={editVariationCost}
                                        setCustomizationVariationToDelete={setCustomizationVariationToDelete}
                                        customizationCalculationPer={customizationCalculationPer}
                                        customizationSizes={customizationSizes}
                                    />
                                ))}
                            </div>
                        </Box>
                        <PageFooter spaceBetween={true}>
                            <>
                                {hasPermission('edit_customization') &&
                                    <Button type={`primary`} size={`small`} text={`Salvar alterações`} svg={`save-white`} action={updateCustomization} />
                                }
                                {hasPermission('delete_customization') &&
                                    <Button type={`secondary`} size={`small`} text={`Excluir`} svg={`delete-small`} action={() => setConfirmDelete(true)} />
                                }
                            </>
                        </PageFooter>
                    </>
                }
            </PanelTemplate>
        </>
    );
}

const Customization = ({
    index,
    customizationCompany,
    customizationCustomer,
    setCompanyToDelete,
    setCustomerToDelete,
    editVariationCost,
    setCustomizationVariationToDelete,
    customizationCalculationPer,
    customizationSizes
}) => {
    const group = customizationCompany ?? customizationCustomer;
    const costs = group.costs;
    const size = customizationCalculationPer === 'size'
        ? (customizationSizes ?? []).find(s => s.id === group.customization_size_id)
        : null;

    return (
        <div key={index} className="col-4">
            <div className="cost-grouping">
                <div className="header">
                    <span>
                        {customizationCompany ? customizationCompany.trade_name : customizationCustomer.name}
                        {customizationCalculationPer === 'size' &&
                            ` — ${size ? size.name : 'Medida removida'}`
                        }
                    </span>
                    {hasPermission('edit_customization') &&
                        <button
                            className="action-delete"
                            onClick={() => {
                                const toDelete = {
                                    id: group.id,
                                    customization_size_id: group.customization_size_id ?? null
                                };
                                if(customizationCompany){
                                    setCompanyToDelete(toDelete);
                                } else {
                                    setCustomerToDelete(toDelete);
                                }
                            }}
                        >
                            <Icon name="delete-small" size={16} />
                        </button>
                    }
                </div>
                <ul>
                    {customizationCalculationPer === 'size' &&
                        <>
                            <li>
                                <div className="cost-desc">
                                    <span>Taxa fixa</span>
                                    <span>R${convertIntToMoney(group.fixed_fee ?? 0)}</span>
                                </div>
                            </li>
                            <li>
                                <div className="cost-desc">
                                    <span>Aplicação por peça</span>
                                    <span>R${convertIntToMoney(group.application_cost ?? 0)}</span>
                                </div>
                            </li>
                        </>
                    }
                    {costs.map((cost, index) => (
                        <li key={index} className={index % 2 === 0 ? 'dark' : ''}>
                            <div className="cost-desc">
                                {customizationCalculationPer === 'thousand' &&
                                    <span>{cost.thousand}º Milheiro</span>
                                }
                                {customizationCalculationPer === 'unit' &&
                                    <span>{cost.init_qtd} a {cost.final_qtd}</span>
                                }
                                {customizationCalculationPer === 'size' &&
                                    <span>
                                        {cost.final_qtd === null
                                            ? `${cost.init_qtd} ou mais`
                                            : `${cost.init_qtd} a ${cost.final_qtd}`
                                        }
                                    </span>
                                }
                                <span>
                                    R${convertIntToMoney(cost.cost)}
                                    {customizationCalculationPer === 'unit' &&
                                        <>
                                            {cost.cost_type === 'interval' &&
                                                `/interv`
                                            }
                                             {cost.cost_type !== 'interval' &&
                                                `/un`
                                            }
                                        </>
                                    }
                                    {customizationCalculationPer === 'size' &&
                                        `/un`
                                    }
                                </span>
                            </div>
                            {/* <button><Icon name="edit" size={16} /></button> */}
                            {hasPermission('edit_customization') &&
                                <button
                                    className="action-edit"
                                    onClick={() => editVariationCost(cost)}
                                >
                                    <Icon name="edit" size={16} />
                                </button>
                            }
                            {hasPermission('edit_customization') &&
                                <button
                                className="action-delete"
                                disabled={index !== (costs.length - 1)}
                                onClick={() => setCustomizationVariationToDelete(cost.id)}
                                >
                                    <Icon name="delete-small" size={16} />
                                </button>
                            }
                        </li>
                    ))}
                    
                </ul>
            </div>
        </div>
    );
}

export default CustomizationEdit;
